import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Edit, Clock } from 'lucide-react';
import { RichTextContent } from '../ui';

export interface AgendaItem {
  id: number;
  title: string;
  description?: string | null;
  type: 'pembahasan' | 'keputusan' | 'informasi';
  duration_minutes: number;
  order: number;
  status?: string;
  laporan_masalah_id?: number | null;
  laporanMasalah?: {
    id: number;
    kode: string;
    judul: string;
  };
}

interface SortableAgendaItemProps {
  item: AgendaItem;
  onEdit?: (item: AgendaItem) => void;
  onDelete?: (id: number) => void;
  isDragging?: boolean;
}

const typeColors = {
  pembahasan: 'bg-blue-100 text-blue-700 border-blue-200',
  keputusan: 'bg-orange-100 text-orange-700 border-orange-200',
  informasi: 'bg-green-100 text-green-700 border-green-200',
};

const typeLabels = {
  pembahasan: 'Pembahasan',
  keputusan: 'Keputusan',
  informasi: 'Informasi',
};

export const SortableAgendaItem: React.FC<SortableAgendaItemProps> = ({
  item,
  onEdit,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg
        ${isDragging ? 'shadow-lg ring-2 ring-blue-400 z-50' : 'shadow-sm hover:shadow-md'}
        transition-shadow
      `}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-1 p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
        title="Seret untuk mengubah urutan"
      >
        <GripVertical size={20} />
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 text-xs font-medium rounded border ${typeColors[item.type]}`}>
            {typeLabels[item.type]}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Clock size={14} />
            {item.duration_minutes} menit
          </span>
        </div>
        
        <h4 className="mt-1 font-medium text-gray-900 truncate">{item.title}</h4>
        
        {item.description && (
          <RichTextContent content={item.description} className="mt-1 text-sm text-gray-600 line-clamp-2" />
        )}
        
        {item.laporanMasalah && (
          <div className="mt-2 text-xs text-gray-500">
            Terkait: <span className="font-medium">{item.laporanMasalah.kode}</span> - {item.laporanMasalah.judul}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        {onEdit && (
          <button
            type="button"
            onClick={() => onEdit(item)}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Edit agenda"
          >
            <Edit size={18} />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={() => onDelete(item.id)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Hapus agenda"
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SortableAgendaItem;
